/**
 * PublisherDashboard — Substack-style writer surface.
 *
 * Route: /publisher/dashboard
 *
 * Sections:
 *   • Stats row: views, claps, followers, articles
 *   • Tab bar: All / Published / Drafts / Scheduled
 *   • Article list with per-row stats + Edit / View links
 *   • Followers strip (top 8)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Eye, Heart, Users, PenLine, Plus, Loader2, ExternalLink,
  BarChart3, FileText, Send, Clock,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function getToken() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token");
}

const TABS = [
  { key: "all", label: "All" },
  { key: "published", label: "Published" },
  { key: "draft", label: "Drafts" },
  { key: "scheduled", label: "Scheduled" },
];

function StatCard({ icon: Icon, label, value, testId, accent = "slate" }) {
  const accentMap = {
    slate: "text-slate-900",
    rose: "text-rose-600",
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    blue: "text-blue-700",
  };
  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-sm transition-shadow"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className={`mt-2 text-3xl font-bold tabular-nums ${accentMap[accent]}`}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    published: "bg-emerald-100 text-emerald-800",
    draft: "bg-slate-100 text-slate-700",
    scheduled: "bg-blue-100 text-blue-800",
    archived: "bg-amber-100 text-amber-800",
    removed: "bg-rose-100 text-rose-800",
  };
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${map[status] || map.draft}`}
      data-testid={`article-status-pill-${status}`}
    >
      {status}
    </span>
  );
}

export default function PublisherDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("all");
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = getToken();
  const hasAuth = !!(user || token);

  const loadStats = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${BASE}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`stats HTTP ${r.status}`);
      const d = await r.json();
      setStats(d);
    } catch (e) {
      setError(e.message);
    }
  }, [token]);

  const loadArticles = useCallback(async () => {
    if (!token) return;
    try {
      const qs = tab === "all" ? "" : `?status=${tab}`;
      const r = await fetch(`${BASE}/dashboard/articles${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`articles HTTP ${r.status}`);
      const d = await r.json();
      setArticles(d.articles || []);
    } catch (e) {
      setError(e.message);
    }
  }, [token, tab]);

  const loadFollowers = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${BASE}/dashboard/followers?limit=8`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const d = await r.json();
      setFollowers(d.followers || []);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => {
    if (!hasAuth) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      await Promise.all([loadStats(), loadArticles(), loadFollowers()]);
      setLoading(false);
    })();
  }, [hasAuth, loadStats, loadArticles, loadFollowers]);

  if (authLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-24 text-center text-slate-500 flex items-center justify-center gap-2">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading dashboard…
      </div>
    );
  }

  if (!hasAuth) {
    return <Navigate to={`/login?next=${encodeURIComponent("/publisher/dashboard")}`} replace />;
  }

  const authorName = user?.name || user?.email || "Writer";

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-8" data-testid="publisher-dashboard">
      {/* Header */}
      <header className="pb-6 mb-6 border-b border-slate-200 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            Writer Dashboard
          </div>
          <h1
            className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1"
            data-testid="publisher-dashboard-heading"
          >
            Welcome back, {authorName}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Your writing surface on SocialPulse.blog.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/publisher/write"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-slate-700 transition-colors"
            data-testid="publisher-dashboard-write-cta"
          >
            <Plus className="w-4 h-4" /> New article
          </Link>
        </div>
      </header>

      {/* Stats row */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <StatCard
          icon={Eye}
          label="Views"
          value={stats?.views_total ?? 0}
          testId="publisher-dashboard-stat-views"
          accent="blue"
        />
        <StatCard
          icon={Heart}
          label="Claps"
          value={stats?.claps_total ?? 0}
          testId="publisher-dashboard-stat-claps"
          accent="rose"
        />
        <StatCard
          icon={Users}
          label="Followers"
          value={stats?.followers ?? 0}
          testId="publisher-dashboard-stat-followers"
          accent="emerald"
        />
        <StatCard
          icon={FileText}
          label="Articles"
          value={stats?.total_articles ?? 0}
          testId="publisher-dashboard-stat-articles"
          accent="amber"
        />
      </section>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-6" role="tablist">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                active
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
              data-testid={`publisher-dashboard-tab-${t.key}`}
            >
              {t.label}
              {t.key === "draft" && stats?.drafts > 0 && (
                <span className="ml-1.5 inline-block bg-slate-200 text-slate-700 text-[10px] rounded-full px-1.5 py-0">
                  {stats.drafts}
                </span>
              )}
              {t.key === "published" && stats?.published > 0 && (
                <span className="ml-1.5 inline-block bg-emerald-100 text-emerald-800 text-[10px] rounded-full px-1.5 py-0">
                  {stats.published}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Article list */}
      {loading ? (
        <div className="py-16 text-center text-slate-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading articles…
        </div>
      ) : error ? (
        <div className="py-8 text-center text-rose-600 text-sm">{error}</div>
      ) : articles.length === 0 ? (
        <div
          className="py-16 text-center text-slate-500 border-2 border-dashed border-slate-200 rounded-2xl"
          data-testid="publisher-dashboard-empty"
        >
          <PenLine className="w-8 h-8 mx-auto mb-3 text-slate-400" />
          <p className="font-semibold text-slate-700">Nothing here yet</p>
          <p className="text-sm mt-1">Start writing your first {tab === "all" ? "article" : tab}.</p>
          <Link
            to="/publisher/write"
            className="inline-flex items-center gap-1.5 mt-4 bg-slate-900 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-slate-700"
          >
            <Plus className="w-4 h-4" /> Write your first article
          </Link>
        </div>
      ) : (
        <ul
          className="divide-y divide-slate-200 border border-slate-200 rounded-2xl overflow-hidden bg-white"
          data-testid="publisher-dashboard-articles-list"
        >
          {articles.map((a) => (
            <li
              key={a.id}
              className="p-5 hover:bg-slate-50 transition-colors"
              data-testid={`publisher-dashboard-article-${a.id}`}
            >
              <div className="flex items-start gap-4">
                {a.cover_image_url ? (
                  <img
                    src={a.cover_image_url}
                    alt=""
                    className="w-24 h-20 object-cover rounded-lg shrink-0 bg-slate-100"
                  />
                ) : (
                  <div className="w-24 h-20 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 shrink-0 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-slate-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusPill status={a.status} />
                    <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                      {a.primary_category}
                    </span>
                    {a.content_format === "note" && (
                      <span className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">
                        Note
                      </span>
                    )}
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mt-1 leading-tight">
                    {a.title}
                  </h3>
                  {a.summary && (
                    <p className="text-sm text-slate-600 mt-1 line-clamp-1">{a.summary}</p>
                  )}
                  <div className="mt-2.5 flex items-center gap-4 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1" data-testid={`article-views-${a.id}`}>
                      <Eye className="w-3 h-3" /> {a.views_total.toLocaleString()}
                    </span>
                    <span className="inline-flex items-center gap-1" data-testid={`article-claps-${a.id}`}>
                      <Heart className="w-3 h-3" /> {a.claps_total.toLocaleString()}
                    </span>
                    {a.published_at ? (
                      <span className="inline-flex items-center gap-1">
                        <Send className="w-3 h-3" />
                        {new Date(a.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                    ) : a.updated_at ? (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(a.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <Link
                    to={`/publisher/write/${a.id}`}
                    className="text-xs font-semibold text-blue-700 hover:underline inline-flex items-center gap-1"
                    data-testid={`publisher-dashboard-edit-${a.id}`}
                  >
                    <PenLine className="w-3 h-3" /> Edit
                  </Link>
                  {a.status === "published" && a.slug && (
                    <Link
                      to={`/blog/${a.slug}`}
                      className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                      data-testid={`publisher-dashboard-view-${a.id}`}
                    >
                      <ExternalLink className="w-3 h-3" /> View
                    </Link>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Followers strip */}
      {followers.length > 0 && (
        <section className="mt-10" data-testid="publisher-dashboard-followers-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-slate-900 inline-flex items-center gap-2">
              <Users className="w-4 h-4" /> Your latest followers
            </h2>
            <span className="text-xs text-slate-500">{stats?.followers ?? 0} total</span>
          </div>
          <ul className="flex items-center gap-3 flex-wrap">
            {followers.map((f) => (
              <li
                key={f.user_id}
                className="bg-white border border-slate-200 rounded-full pl-1 pr-4 py-1 flex items-center gap-2"
                data-testid={`publisher-dashboard-follower-${f.user_id}`}
              >
                {f.photo_url ? (
                  <img src={f.photo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 text-white text-xs font-bold flex items-center justify-center">
                    {(f.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <span className="text-xs font-semibold text-slate-800">{f.name}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
